import { gsap } from 'gsap';

import { getStartTime } from 'js/playback/helpers/timings';
import { ScribeCameraElement } from 'js/types';
import ScribeModel from 'js/models/ScribeModel';
import { toSeconds } from 'js/editor/PlayerContext/timingHelpers';
import { CAMERA_EASING_NONE } from 'js/config/consts';

interface CameraPosition {
  x: number;
  y: number;
  scale: number;
}

/**
 * Calculates the camera position at a given time by interpolating between
 * camera keyframes. This is used to initialize the viewport when starting
 * playback mid-way through an animation.
 *
 * @param cameras - Array of camera elements in the scene, in order
 * @param currentTimeSeconds - The current playback time in seconds (relative to scene start)
 * @param project - The project model for timing calculations
 * @param sceneStartTimeSeconds - The scene's start time in seconds
 * @returns The interpolated camera position
 */
export function getCameraPositionAtTime(
  cameras: ScribeCameraElement[],
  currentTimeSeconds: number,
  project: ScribeModel,
  sceneStartTimeSeconds: number
): CameraPosition {
  if (cameras.length === 0) {
    return { x: 0, y: 0, scale: 1 };
  }

  // First camera is always the initial position
  if (cameras.length === 1) {
    return {
      x: cameras[0].x,
      y: cameras[0].y,
      scale: cameras[0].scale
    };
  }

  // Find which camera transition we're in (or past)
  for (let i = 1; i < cameras.length; i++) {
    const camera = cameras[i];
    const previousCamera = cameras[i - 1];

    const animationDuration = camera.animationTime ?? 0;
    const animationAbsoluteStartTime = toSeconds(getStartTime(project, camera.id));
    const animationRelativeStartTime = animationAbsoluteStartTime - sceneStartTimeSeconds;
    const animationEndTime = animationRelativeStartTime + animationDuration;

    // If we're before this camera's animation starts, we're at the previous camera position
    if (currentTimeSeconds < animationRelativeStartTime) {
      return {
        x: previousCamera.x,
        y: previousCamera.y,
        scale: previousCamera.scale
      };
    }

    // If we're within this camera's animation, interpolate
    if (currentTimeSeconds >= animationRelativeStartTime && currentTimeSeconds < animationEndTime) {
      // Handle instant transitions (no duration or "no-ease-no-duration")
      if (animationDuration === 0 || camera.easingType === CAMERA_EASING_NONE) {
        return {
          x: camera.x,
          y: camera.y,
          scale: camera.scale
        };
      }

      // Calculate progress through the animation (0 to 1)
      const linearProgress = (currentTimeSeconds - animationRelativeStartTime) / animationDuration;

      // Apply easing to get the actual interpolation progress
      const ease = gsap.parseEase(camera.easingType || 'power2.out');
      const easedProgress = ease(linearProgress);

      // Interpolate in screen-space (same as the animation does)
      const startScreenX = previousCamera.x * previousCamera.scale;
      const startScreenY = previousCamera.y * previousCamera.scale;
      const endScreenX = camera.x * camera.scale;
      const endScreenY = camera.y * camera.scale;

      const interpolatedScreenX = startScreenX + (endScreenX - startScreenX) * easedProgress;
      const interpolatedScreenY = startScreenY + (endScreenY - startScreenY) * easedProgress;
      const interpolatedScale = previousCamera.scale + (camera.scale - previousCamera.scale) * easedProgress;

      // Convert back to world-space
      return {
        x: interpolatedScreenX / interpolatedScale,
        y: interpolatedScreenY / interpolatedScale,
        scale: interpolatedScale
      };
    }
  }

  // If we're past all animations, return the last camera position
  const lastCamera = cameras[cameras.length - 1];
  return {
    x: lastCamera.x,
    y: lastCamera.y,
    scale: lastCamera.scale
  };
}
